import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Warning } from "@phosphor-icons/react";

import Header from "@/components/Header";
import HeadlineTicker from "@/components/HeadlineTicker";
import VideoSidebar from "@/components/VideoSidebar";
import ClaimCard from "@/components/ClaimCard";
import VerdictReportCard from "@/components/VerdictReportCard";
import FactCheckerToggle from "@/components/FactCheckerToggle";
import { isFactCheckerSource } from "@/lib/factCheckers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharedResultPage() {
  const { cacheKey } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [factCheckersOnly, setFactCheckersOnly] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/v1/fact-check/${cacheKey}`)
      .then((r) => {
        if (!mounted) return;
        setResult(r.data);
        setError(null);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(
          e.response?.status === 404
            ? "This fact-check link has expired or does not exist. Cached checks are auto-removed after their TTL."
            : "Could not load this fact-check right now."
        );
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [cacheKey]);

  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900 relative">
      <Header />
      <HeadlineTicker />

      <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-10 pb-4 relative z-10">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-700 hover:text-zinc-950 mb-6"
        >
          <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
          BACK TO FACT-CHECKER
        </Link>
        <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-2">
          SHARED FACT-CHECK · CACHED VERDICT
        </div>
        <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tight text-zinc-950">
          The receipts.
        </h1>
      </section>

      {loading && (
        <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-24">
          <div className="border border-zinc-200 bg-white p-8 animate-pulse h-64" />
        </div>
      )}

      {error && (
        <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-24">
          <div
            data-testid="shared-error"
            className="border-2 border-red-500 bg-red-50 text-red-900 px-6 py-5 flex items-start gap-3"
          >
            <Warning weight="fill" className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-mono-plex text-[11px] font-semibold mb-1">FACT-CHECK NOT FOUND</div>
              <p className="text-sm">{error}</p>
              <Link
                to="/"
                className="inline-block mt-3 font-mono-plex text-[10px] font-semibold underline hover:text-red-700"
              >
                RUN A NEW FACT-CHECK →
              </Link>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && result && (
        <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <VideoSidebar
                metadata={result.video_metadata}
                videoUrl={result.video_url}
                overallVerdict={result.overall_verdict}
                overallConfidence={result.overall_confidence}
                overallSummary={result.overall_summary}
                cached
                processingMs={result.processing_time_ms}
                transcriptExcerpt={result.transcript_excerpt}
                cacheKey={result.cache_key}
              />
            </div>
          </div>

          <div className="lg:col-span-8 flex flex-col gap-6">
            {result.propaganda && (
              <VerdictReportCard
                propaganda={result.propaganda}
                factualAccuracy={result.factual_accuracy || 0}
                trustScore={result.trust_score || 0}
                claims={result.claims || []}
                overallVerdict={result.overall_verdict}
                overallConfidence={result.overall_confidence}
                videoMetadata={result.video_metadata}
                cacheKey={result.cache_key}
              />
            )}
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="font-mono-plex text-[10px] font-semibold text-zinc-500">
                FINDINGS · {result.claims?.length || 0} CLAIM{(result.claims?.length || 0) === 1 ? "" : "S"}
              </div>
              {(result.claims || []).length > 0 && (
                <FactCheckerToggle
                  enabled={factCheckersOnly}
                  onChange={setFactCheckersOnly}
                  matchCount={(result.claims || []).reduce(
                    (n, c) => n + (c.sources || []).filter(isFactCheckerSource).length,
                    0
                  )}
                  totalCount={(result.claims || []).reduce((n, c) => n + (c.sources?.length || 0), 0)}
                />
              )}
            </div>
            {(result.claims || []).length === 0 && (
              <div className="border border-zinc-300 bg-white p-8 text-zinc-700">
                No verifiable claims were detected in this video.
              </div>
            )}
            {(result.claims || []).map((c, i) => (
              <ClaimCard key={c.claim_id} claim={c} index={i} factCheckersOnly={factCheckersOnly} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
